import { Option } from 'approot/shared/pills/pills-picker.hooks';
import type { GetStartupDataQuery } from '../graphql/startup-data/startup-data.graphql.generated';
import type { TermFragment } from '../../classes/classes.graphql.generated';
import type { Pluck } from 'lib/gql/pluck';

export const getAcademicYearOptions = ({
  currentYear,
  academicYears,
  classTeachingYear,
}: {
  currentYear: number;
  academicYears: Pluck<GetStartupDataQuery, 'startupData.academicYears'>[];
  classTeachingYear?: number | null;
}): Option[] => {
  const years: Option[] = [];
  // classTeachingYear is used when editing a class, and is the year that the class was created

  // yearToStart is used to determine the start year for the returned year options
  // and will be either the current calendar year or the classTeachingYear being edited (whichever is earlier)
  const yearToStart =
    !!classTeachingYear && classTeachingYear < currentYear
      ? classTeachingYear
      : currentYear;

  // isCurrentTeachingYear flags the academic year that is currently in progress
  // US academic years start in July/August, so this value will change in the middle of the year
  // e.g. if the current calendar year is 2023, the US current teaching year will be 2022 (2022/2023) until July/August
  // when it will change to 2023 (2023/2024)

  // startIndex will be the index of the academic year that matches the current calendar year, or
  // the classTeachingYear (edit class), or
  // the current teaching year (for the US, this calendar year or the previous calendar year)
  // whichever is earlier

  const startIndex = academicYears.findIndex(
    ay => ay.academicYear === yearToStart || ay.isCurrentTeachingYear
  );

  for (let i = startIndex; i < academicYears.length; i++) {
    const startYear = academicYears[i].startTeachingYear;
    const endYear = academicYears[i].endTeachingYear;
    const label = startYear !== endYear ? `${startYear}/${endYear}` : startYear;

    // stop one year after the current year
    if (startYear > currentYear + 1) {
      break;
    }

    years.push({
      label,
      value: `${academicYears[i].academicYear}`,
    });
  }
  return years;
};

export function formatTeachingYear(
  terms?: TermFragment[],
  options?: { formatShortYear: boolean }
) {
  const startYearTerm = new Date(terms?.at(0)?.start || new Date());
  const endYearTerm = new Date(terms?.at(-1)?.end || new Date());
  if (startYearTerm.getFullYear() !== endYearTerm.getFullYear()) {
    if (options?.formatShortYear) {
      return `${startYearTerm.getFullYear()}/${`${endYearTerm.getFullYear()}`.substring(
        2,
        4
      )}`;
    }
    return `${startYearTerm.getFullYear()}/${endYearTerm.getFullYear()}`;
  }
  return startYearTerm.getFullYear().toString();
}

export function getCurrentAcademicYear({
  academicYears,
  isSingleYearFormat,
}: {
  academicYears:
    | Pluck<GetStartupDataQuery, 'startupData.academicYears'>[]
    | undefined;
  isSingleYearFormat?: boolean;
}) {
  const currentTeachingYearData = academicYears?.find(
    ay => !!ay.isCurrentTeachingYear
  );
  const startYear = currentTeachingYearData?.startTeachingYear;
  const endYear = currentTeachingYearData?.endTeachingYear;

  if (isSingleYearFormat) {
    return currentTeachingYearData?.academicYear;
  }
  return startYear !== endYear ? `${startYear}/${endYear}` : startYear;
}
